/* ==========================================================================
   Diagnosis Code Search Component
   Searchable ICD-10 code selector with lazy-loaded data
   ========================================================================== */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'

interface ICD10Code {
  code: string
  description: string
}

let cachedCodes: ICD10Code[] | null = null

async function loadICD10Codes(): Promise<ICD10Code[]> {
  if (cachedCodes) return cachedCodes
  try {
    const response = await fetch('/data/icd10.json')
    cachedCodes = await response.json()
    return cachedCodes!
  } catch {
    console.error('Failed to load ICD-10 codes')
    return []
  }
}

interface DiagnosisCodeSearchProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  /** Show the required marker (PORTAL-202). */
  required?: boolean
}

export function DiagnosisCodeSearch({
  value,
  onChange,
  disabled = false,
  required = false,
}: DiagnosisCodeSearchProps) {
  const [query, setQuery] = useState(value)
  const [codes, setCodes] = useState<ICD10Code[]>([])
  const [results, setResults] = useState<ICD10Code[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Sync external value changes
  useEffect(() => {
    setQuery(value)
  }, [value])

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load codes on first focus
  const handleFocus = useCallback(async () => {
    if (codes.length === 0) {
      setIsLoading(true)
      const loaded = await loadICD10Codes()
      setCodes(loaded)
      setIsLoading(false)
    }
  }, [codes.length])

  // Search with debounce
  const handleInputChange = useCallback(
    (inputValue: string) => {
      setQuery(inputValue)

      if (debounceRef.current) clearTimeout(debounceRef.current)

      debounceRef.current = setTimeout(() => {
        if (!inputValue.trim()) {
          setResults([])
          setIsOpen(false)
          onChange('')
          return
        }

        const search = inputValue.toLowerCase()
        const filtered = codes.filter(
          (c) =>
            c.code.toLowerCase().includes(search) ||
            c.description.toLowerCase().includes(search)
        )
        setResults(filtered.slice(0, 20))
        setIsOpen(filtered.length > 0)
      }, 200)
    },
    [codes, onChange]
  )

  const handleSelect = useCallback(
    (code: ICD10Code) => {
      const display = `${code.code} - ${code.description}`
      setQuery(display)
      onChange(display)
      setIsOpen(false)
    },
    [onChange]
  )

  const handleClear = useCallback(() => {
    setQuery('')
    onChange('')
    setResults([])
    setIsOpen(false)
  }, [onChange])

  return (
    <div ref={wrapperRef} className="relative">
      <FormField
        label="Diagnosis Code (ICD-10)"
        htmlFor="diagnosis_code"
        required={required}
        helperText="Search by code or description"
      >
        <div className="relative">
          <Input
            id="diagnosis_code"
            type="text"
            placeholder="Search ICD-10 codes..."
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            disabled={disabled}
            autoComplete="off"
          />
          {query && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] hover:text-[var(--color-charcoal)] text-sm"
            >
              &times;
            </button>
          )}
        </div>
      </FormField>

      {isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
          Loading ICD-10 codes...
        </div>
      )}

      {isOpen && results.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {results.map((code) => (
            <li
              key={code.code}
              onClick={() => handleSelect(code)}
              className="px-3 py-2 hover:bg-[var(--color-soft-lavender)] cursor-pointer text-sm border-b border-gray-100 last:border-0"
            >
              <span className="font-medium text-[var(--pharmacy-primary)]">
                {code.code}
              </span>
              <span className="text-[var(--color-charcoal)] ml-2">
                {code.description}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default DiagnosisCodeSearch
